import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api, { contentAPI } from '../../services/api';
import SelectInput from '../../components/form/SelectInput';
import TextInput from '../../components/form/TextInput';
import TextArea from '../../components/form/TextArea';
import PhoneInput from '../../components/form/PhoneInput';
import EmailInput from '../../components/form/EmailInput';
import CheckboxInput from '../../components/form/CheckboxInput';
import '../../components/form/FormStyles.css';
import './AdminStaffMemberForm.css';
import { RichTextEditor } from './Content';

const departmentTypeOptions = [
  { value: 'administration', label: 'Administrative (प्रशासनिक)' },
  { value: 'health', label: 'Health (आरोग्य)' },
  { value: 'animal_husbandry', label: 'Animal Husbandry (पशुसंवर्धन)' },
  { value: 'education', label: 'Education (शिक्षा)' },
  { value: 'social_welfare', label: 'Social Welfare (सामाजिक कल्याण)' },
  { value: 'agriculture', label: 'Agriculture (कृषी)' },
  { value: 'water_and_sanitation', label: 'Water & Sanitation (पाणीपुरवठा व स्वच्छता)' },
  { value: 'pwd', label: 'Public Works (सार्वजनिक बांधकाम)' },
  { value: 'rural_development', label: 'Rural Development (ग्रामविकास)' },
  { value: 'finance', label: 'Finance (वित्त)' },
  { value: 'women_child', label: 'Women & Child Development (महिला व बाल विकास)' },
  { value: 'tourism', label: 'Tourism (पर्यटन)' },
  { value: 'other', label: 'Other (इतर)' },
];

const commonDocuments = [
  { value: 'aadhar', label: 'Aadhar Card' },
  { value: 'pan', label: 'PAN Card' },
  { value: 'passport', label: 'Passport' },
  { value: 'voter_id', label: 'Voter ID' },
  { value: 'driving_license', label: 'Driving License' },
  { value: 'property_document', label: 'Property Document' },
  { value: 'bank_passbook', label: 'Bank Passbook' },
  { value: 'electricity_bill', label: 'Electricity Bill' },
  { value: 'water_bill', label: 'Water Bill' },
  { value: 'tax_certificate', label: 'Tax Certificate' },
  { value: 'caste_certificate', label: 'Caste Certificate' },
  { value: 'income_certificate', label: 'Income Certificate' },
  { value: 'business_license', label: 'Business License' },
  { value: 'agricultural_land_document', label: 'Agricultural Land Document' },
];

const documentLabel = value => commonDocuments.find(doc => doc.value === value)?.label || value;
const normalizeDocuments = value => Array.isArray(value) ? value.filter(Boolean) : [];

const apiErrorMessage = (error, fallback) => {
  const details = error.response?.data?.details;
  if (Array.isArray(details) && details.length) {
    return details.map(item => `${item.field || 'field'}: ${item.message}`).join(', ');
  }
  return error.response?.data?.error || fallback;
};

const blankContentSection = () => ({
  id: Date.now() + Math.random(),
  heading: '',
  content: '',
  image_url: '',
  image_caption: '',
  order: 0,
});

const normalizeContentSections = (value) => {
  if (!value) return [];
  let sections = value;
  if (typeof value === 'string') {
    try {
      sections = JSON.parse(value);
    } catch {
      return [];
    }
  }
  if (!Array.isArray(sections)) return [];
  return sections.map((section, index) => ({
    id: section.id || Date.now() + index,
    heading: section.heading || '',
    content: section.content || '',
    image_url: section.image_url || '',
    image_caption: section.image_caption || '',
    order: Number(section.order ?? index),
  })).sort((a, b) => a.order - b.order);
};

const normalizeImages = (value) => {
  if (!value) return [];
  let items = value;
  if (typeof value === 'string') {
    try {
      items = JSON.parse(value);
    } catch {
      return [];
    }
  }
  if (!Array.isArray(items)) return [];
  return items.map((item, index) => {
    if (typeof item === 'string') {
      return { id: `img-${Date.now()}-${index}`, url: item.trim(), caption: '' };
    }
    return {
      id: item?.id || `img-${Date.now()}-${index}`,
      url: String(item?.url || item?.image_url || '').trim(),
      caption: String(item?.caption || item?.image_caption || '').trim(),
    };
  }).filter(item => item && item.url);
};



export default function AdminDepartmentForm() {
  const { tenantSlug, departmentId } = useParams();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: '',
    name_english: '',
    department_type: 'administration',
    description: '',
    icon: '',
    head_name: '',
    head_designation: '',
    head_phone: '',
    head_email: '',
    office_phone: '',
    office_email: '',
    office_location: '',
    office_address: '',
    services_offered: '',
    documents_required: [],
    content_sections: [],
    images: [],
    office_hours: '9:00 AM - 5:30 PM',
    sort_order: 0,
    is_active: true,
  });

  const [errors, setErrors] = useState({});
  const [newDocumentName, setNewDocumentName] = useState('');
  const [loading, setLoading] = useState(departmentId ? true : false);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [sectionImageUploading, setSectionImageUploading] = useState(false);
  const [sectionImageUploadTarget, setSectionImageUploadTarget] = useState(null);
  const sectionImageUploadRef = useRef(null);
  const [galleryImageUploading, setGalleryImageUploading] = useState(false);
  const galleryImageUploadRef = useRef(null);
  const [newImageUrl, setNewImageUrl] = useState('');
  const [newImageCaption, setNewImageCaption] = useState('');
  const steps = [
    { title: 'मूलभूत माहिती', subtitle: 'Basic details' },
    { title: 'विभाग प्रमुख', subtitle: 'Department head' },
    { title: 'कार्यालय माहिती', subtitle: 'Office details' },
    { title: 'सेवा व सामग्री', subtitle: 'Services & content' },
    { title: 'पुनरावलोकन', subtitle: 'Status & review' },
  ];

  // Fetch existing department
  useEffect(() => {
    if (departmentId) {
      const fetchDepartment = async () => {
        try {
          const res = await api.get(`/tenant/${tenantSlug}/departments/admin/${departmentId}`);
          setFormData({
            ...res.data,
            documents_required: normalizeDocuments(res.data.documents_required),
            content_sections: normalizeContentSections(res.data.content_sections),
            images: normalizeImages(res.data.images),
            is_active: res.data.is_active !== false,
          });
        } catch (error) {
          setErrors({ submit: 'Failed to load department' });
        } finally {
          setLoading(false);
        }
      };
      fetchDepartment();
    }
  }, [departmentId, tenantSlug]);

  const validateForm = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = 'Department name is required';
    if (!formData.department_type) newErrors.department_type = 'Department type is required';
    if (formData.head_phone && formData.head_phone.replace(/\D/g, '').length !== 10) {
      newErrors.head_phone = 'Valid 10-digit phone is required';
    }
    if (formData.head_email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.head_email)) {
      newErrors.head_email = 'Invalid email format';
    }
    if (formData.office_phone && formData.office_phone.replace(/\D/g, '').length !== 10) {
      newErrors.office_phone = 'Valid 10-digit phone is required';
    }
    if (formData.office_email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.office_email)) {
      newErrors.office_email = 'Invalid email format';
    }
    return newErrors;
  };

  const stepFields = [
    ['name', 'department_type'],
    ['head_phone', 'head_email'],
    ['office_phone', 'office_email'],
    [],
    [],
  ];

  const validateStep = (stepIndex) => {
    const allErrors = validateForm();
    return stepFields[stepIndex].reduce((result, field) => {
      if (allErrors[field]) result[field] = allErrors[field];
      return result;
    }, {});
  };

  const moveStep = (nextStep) => {
    if (nextStep > currentStep) {
      const stepErrors = validateStep(currentStep);
      if (Object.keys(stepErrors).length) {
        setErrors(current => ({ ...current, ...stepErrors }));
        return;
      }
    }
    setCurrentStep(Math.max(0, Math.min(nextStep, steps.length - 1)));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: '',
      }));
    }
  };

  const handleDocumentToggle = (documentValue) => {
    setFormData((prev) => {
      const currentDocs = prev.documents_required || [];
      const newDocs = currentDocs.includes(documentValue)
        ? currentDocs.filter((d) => d !== documentValue)
        : [...currentDocs, documentValue];
      return {
        ...prev,
        documents_required: newDocs,
      };
    });
  };

  const addCustomDocument = () => {
    const trimmed = newDocumentName.trim();
    if (!trimmed) return;
    setFormData(prev => {
      const currentDocs = normalizeDocuments(prev.documents_required);
      if (currentDocs.some(doc => doc.toLowerCase() === trimmed.toLowerCase())) {
        return prev;
      }
      return {
        ...prev,
        documents_required: [...currentDocs, trimmed],
      };
    });
    setNewDocumentName('');
  };

  const removeDocument = (documentValue) => {
    setFormData(prev => ({
      ...prev,
      documents_required: normalizeDocuments(prev.documents_required).filter(doc => doc !== documentValue),
    }));
  };

  const updateContentSection = (sectionId, field, value) => {
    setFormData(prev => ({
      ...prev,
      content_sections: normalizeContentSections(prev.content_sections).map(section =>
        section.id === sectionId ? { ...section, [field]: value } : section
      ),
    }));
  };

  const validateImageFile = (file) => {
    if (!file) return 'कृपया प्रतिमा निवडा.';
    if (!file.type.startsWith('image/')) return 'केवळ प्रतिमा फायली स्वीकारल्या जातात.';
    if (file.size > 5 * 1024 * 1024) return 'प्रतिमा 5MB पेक्षा लहान असावी.';
    return null;
  };

  const uploadImageFile = async (file) => {
    const validationError = validateImageFile(file);
    if (validationError) throw new Error(validationError);
    const form = new FormData();
    form.append('image', file);
    const response = await contentAPI.uploadImage(tenantSlug, form);
    return response.data?.url;
  };

  const handleSectionImageUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file || !sectionImageUploadTarget) return;
    event.target.value = '';
    setSectionImageUploading(true);
    try {
      const url = await uploadImageFile(file);
      if (url) {
        updateContentSection(sectionImageUploadTarget, 'image_url', url);
      }
    } catch (error) {
      setErrors(prev => ({ ...prev, submit: error.message || 'Section image upload failed.' }));
    } finally {
      setSectionImageUploading(false);
      setSectionImageUploadTarget(null);
    }
  };

  const beginSectionImageUpload = (sectionId) => {
    setSectionImageUploadTarget(sectionId);
    sectionImageUploadRef.current?.click();
  };

  const handleGalleryImageUpload = async (event) => {
    const files = Array.from(event.target.files || []);
    if (!files.length) return;
    event.target.value = '';
    setGalleryImageUploading(true);
    try {
      const uploadedList = [];
      for (let i = 0; i < files.length; i += 1) {
        const url = await uploadImageFile(files[i]);
        if (url) {
          uploadedList.push({
            id: `img-${Date.now()}-${i}-${Math.floor(Math.random() * 1000)}`,
            url,
            caption: '',
          });
        }
      }
      if (uploadedList.length > 0) {
        setFormData(prev => ({
          ...prev,
          images: [...normalizeImages(prev.images), ...uploadedList],
        }));
      }
    } catch (error) {
      setErrors(prev => ({ ...prev, submit: error.message || 'Gallery image upload failed.' }));
    } finally {
      setGalleryImageUploading(false);
    }
  };

  const addImageByUrl = () => {
    const trimmedUrl = newImageUrl.trim();
    if (!trimmedUrl) return;
    setFormData(prev => ({
      ...prev,
      images: [
        ...normalizeImages(prev.images),
        {
          id: `img-${Date.now()}-${Math.floor(Math.random() * 1000)}`,
          url: trimmedUrl,
          caption: newImageCaption.trim(),
        },
      ],
    }));
    setNewImageUrl('');
    setNewImageCaption('');
  };

  const updateImageCaption = (imageId, caption) => {
    setFormData(prev => ({
      ...prev,
      images: normalizeImages(prev.images).map(img =>
        img.id === imageId ? { ...img, caption } : img
      ),
    }));
  };

  const removeImage = (imageId) => {
    setFormData(prev => ({
      ...prev,
      images: normalizeImages(prev.images).filter(img => img.id !== imageId),
    }));
  };

  const moveImage = (index, direction) => {
    setFormData(prev => {
      const items = normalizeImages(prev.images);
      const target = index + direction;
      if (target < 0 || target >= items.length) return prev;
      const reordered = [...items];
      [reordered[index], reordered[target]] = [reordered[target], reordered[index]];
      return {
        ...prev,
        images: reordered,
      };
    });
  };

  const addContentSection = () => {
    setFormData(prev => {
      const sections = normalizeContentSections(prev.content_sections);
      return {
        ...prev,
        content_sections: [...sections, { ...blankContentSection(), order: sections.length }],
      };
    });
  };

  const removeContentSection = (sectionId) => {
    setFormData(prev => ({
      ...prev,
      content_sections: normalizeContentSections(prev.content_sections)
        .filter(section => section.id !== sectionId)
        .map((section, index) => ({ ...section, order: index })),
    }));
  };

  const moveContentSection = (index, direction) => {
    setFormData(prev => {
      const sections = normalizeContentSections(prev.content_sections);
      const target = index + direction;
      if (target < 0 || target >= sections.length) return prev;
      const reordered = [...sections];
      [reordered[index], reordered[target]] = [reordered[target], reordered[index]];
      return {
        ...prev,
        content_sections: reordered.map((section, order) => ({ ...section, order })),
      };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Guard: Never auto-submit unless on the final review step (पुनरावलोकन)
    if (currentStep < steps.length - 1) {
      moveStep(currentStep + 1);
      return;
    }

    const newErrors = validateForm();
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      const invalidStep = steps.findIndex((_, index) => Object.keys(validateStep(index)).length);
      if (invalidStep >= 0) setCurrentStep(invalidStep);
      return;
    }

    try {
      setSubmitting(true);
      const method = departmentId ? 'put' : 'post';
      const endpoint = departmentId
        ? `/tenant/${tenantSlug}/departments/${departmentId}`
        : `/tenant/${tenantSlug}/departments`;

      const payload = {
        ...formData,
        images: normalizeImages(formData.images),
        content_sections: normalizeContentSections(formData.content_sections)
          .filter(section => section.heading.trim() || section.content.trim() || section.image_url.trim())
          .map((section, index) => ({ ...section, order: index })),
      };

      await api[method](endpoint, payload);
      setSuccess(true);
      setTimeout(() => {
        navigate(`/admin/${tenantSlug}/departments`);
      }, 1500);
    } catch (error) {
      setErrors({ submit: apiErrorMessage(error, 'Failed to save department') });
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return <div className="form-container staff-member-form department-wizard-form">Loading...</div>;
  }

  return (
    <div className="form-container staff-member-form department-wizard-form">
      <h1>{departmentId ? 'Edit Department' : 'Add New Department'}</h1>

      {success && <div className="success-message">Department saved successfully!</div>}
      {errors.submit && <div style={{ color: '#e74c3c', marginBottom: '20px' }}>{errors.submit}</div>}

      <form
        onSubmit={handleSubmit}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && e.target.tagName !== 'TEXTAREA') {
            e.preventDefault();
          }
        }}
      >
        <nav className="staff-form-stepper" aria-label="Department form steps">
          {steps.map((step, index) => (
            <button
              type="button"
              key={step.title}
              className={`${index === currentStep ? 'active' : ''} ${index < currentStep ? 'completed' : ''}`}
              disabled={index > currentStep}
              onClick={() => moveStep(index)}
              aria-current={index === currentStep ? 'step' : undefined}
            >
              <span>{index < currentStep ? '✓' : index + 1}</span>
              <div><strong>{step.title}</strong><small>{step.subtitle}</small></div>
            </button>
          ))}
        </nav>
        <div className="form-inner">
        {/* Basic Information */}
        {currentStep === 0 && <div className="form-section staff-step-panel">
          <h3>मूलभूत माहिती (Basic Information)</h3>

          <TextInput
            label="Department Name (Marathi / Display) (विभाग नाव) *"
            name="name"
            value={formData.name}
            onChange={handleChange}
            error={errors.name}
            placeholder="उदा. आरोग्य विभाग"
            required
          />

          <TextInput
            label="Department Name (English)"
            name="name_english"
            value={formData.name_english || ''}
            onChange={handleChange}
            placeholder="e.g., Health Department"
          />

          <SelectInput
            label="Department Type (विभाग प्रकार) *"
            name="department_type"
            value={formData.department_type}
            onChange={handleChange}
            options={departmentTypeOptions}
            error={errors.department_type}
            required
          />

          <TextArea
            label="Description (वर्णन)"
            name="description"
            value={formData.description}
            onChange={handleChange}
            rows={3}
            placeholder="Brief description of the department's role and responsibilities"
          />

          <TextInput
            label="Icon / Image URL (optional)"
            name="icon"
            value={formData.icon || ''}
            onChange={handleChange}
            placeholder="https://example.com/department-image.png"
          />
        </div>}

        {/* Department Head Information */}
        {currentStep === 1 && <div className="form-section staff-step-panel">
          <h3>विभाग प्रमुख माहिती (Department Head Information)</h3>

          <TextInput
            label="Head Name (प्रमुख नाव)"
            name="head_name"
            value={formData.head_name}
            onChange={handleChange}
            placeholder="Full name of department head"
          />

          <TextInput
            label="Head Designation (प्रमुख पद)"
            name="head_designation"
            value={formData.head_designation}
            onChange={handleChange}
            placeholder="e.g., Health Officer, Agriculture Officer"
          />

          <PhoneInput
            label="Head Phone (प्रमुख फोन)"
            name="head_phone"
            value={formData.head_phone}
            onChange={handleChange}
            error={errors.head_phone}
          />

          <EmailInput
            label="Head Email (प्रमुख ईमेल)"
            name="head_email"
            value={formData.head_email}
            onChange={handleChange}
            error={errors.head_email}
          />
        </div>}

        {/* Office Information */}
        {currentStep === 2 && <div className="form-section staff-step-panel">
          <h3>कार्यालय माहिती (Office Information)</h3>

          <PhoneInput
            label="Office Phone (कार्यालय फोन)"
            name="office_phone"
            value={formData.office_phone}
            onChange={handleChange}
            error={errors.office_phone}
          />

          <EmailInput
            label="Office Email (कार्यालय ईमेल)"
            name="office_email"
            value={formData.office_email}
            onChange={handleChange}
            error={errors.office_email}
          />

          <TextInput
            label="Office Location (कार्यालय स्थान)"
            name="office_location"
            value={formData.office_location}
            onChange={handleChange}
            placeholder="e.g., Ground Floor, Main Building"
          />

          <TextArea
            label="Office Address (कार्यालय पता)"
            name="office_address"
            value={formData.office_address}
            onChange={handleChange}
            rows={2}
            placeholder="Complete office address"
          />

          <TextInput
            label="Working Hours (कार्य तास)"
            name="office_hours"
            value={formData.office_hours || ''}
            onChange={handleChange}
            placeholder="e.g., 9:00 AM - 5:30 PM"
          />

          <TextInput
            label="Display Order (क्रम)"
            name="sort_order"
            type="number"
            value={formData.sort_order || 0}
            onChange={handleChange}
            placeholder="0"
          />
        </div>}

        {/* Services & Documents */}
        {currentStep === 3 && <div className="form-section staff-step-panel department-services-step">
          <h3>सेवा व कागदपत्रे (Services & Documents)</h3>

          <TextArea
            label="Services Offered (दिली जाणारी सेवा)"
            name="services_offered"
            value={formData.services_offered}
            onChange={handleChange}
            rows={4}
            placeholder="List all services offered by this department (one per line)"
          />

          <div className="department-documents-panel">
            <label style={{ display: 'block', marginBottom: '10px', fontWeight: '600', color: '#333' }}>
              Documents Required (आवश्यक कागदपत्रे)
            </label>
            <div className="department-doc-grid">
              {commonDocuments.map((doc) => (
                <CheckboxInput
                  key={doc.value}
                  label={doc.label}
                  name={`doc_${doc.value}`}
                  checked={formData.documents_required.includes(doc.value)}
                  onChange={() => handleDocumentToggle(doc.value)}
                />
              ))}
            </div>

            <div className="department-custom-doc-row">
              <input
                type="text"
                value={newDocumentName}
                onChange={event => setNewDocumentName(event.target.value)}
                onKeyDown={event => {
                  if (event.key === 'Enter') {
                    event.preventDefault();
                    addCustomDocument();
                  }
                }}
                placeholder="इतर कागदपत्राचे नाव लिहा..."
              />
              <button type="button" onClick={addCustomDocument}>+ कागदपत्र जोडा</button>
            </div>

            {normalizeDocuments(formData.documents_required).length > 0 && (
              <div className="department-selected-docs">
                <strong>Selected Documents ({normalizeDocuments(formData.documents_required).length}):</strong>
                <div>
                  {normalizeDocuments(formData.documents_required).map(doc => (
                    <span key={doc}>
                      {documentLabel(doc)}
                      <button type="button" onClick={() => removeDocument(doc)} aria-label={`Remove ${documentLabel(doc)}`}>×</button>
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Department Images / Gallery */}
            <div className="department-gallery-panel">
              <div className="dept-section-head department-section-head-inline">
                <div>
                  <h3>विभागाची छायाचित्रे (Department Images / Gallery)</h3>
                  <p>विभाग तपशील पृष्ठावर कॅरोसेल (Carousel) स्वरूपात दाखवण्यासाठी छायाचित्रे जोडा. कोणतीही प्रतिमा न जोडल्यास कॅरोसेल दिसणार नाही.</p>
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button
                    type="button"
                    className="btn btn-outline"
                    onClick={() => galleryImageUploadRef.current?.click()}
                    disabled={galleryImageUploading}
                  >
                    {galleryImageUploading ? 'प्रतिमा अपलोड होत आहेत...' : '📷 छायाचित्रे अपलोड करा (Upload)'}
                  </button>
                  <input
                    ref={galleryImageUploadRef}
                    type="file"
                    accept="image/*"
                    multiple
                    style={{ display: 'none' }}
                    onChange={handleGalleryImageUpload}
                  />
                </div>
              </div>

              <div className="dept-gallery-url-bar">
                <input
                  type="url"
                  value={newImageUrl}
                  onChange={e => setNewImageUrl(e.target.value)}
                  placeholder="किंवा थेट प्रतिमेची URL टाका (https://...)"
                  style={{ flex: 2 }}
                />
                <input
                  type="text"
                  value={newImageCaption}
                  onChange={e => setNewImageCaption(e.target.value)}
                  placeholder="प्रतिमा वर्णन / Caption (ऐच्छिक)"
                  style={{ flex: 1.5 }}
                />
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={addImageByUrl}
                  disabled={!newImageUrl.trim()}
                >
                  + URL जोडा
                </button>
              </div>

              {normalizeImages(formData.images).length === 0 ? (
                <div className="dept-section-empty">
                  अद्याप कोणतीही छायाचित्रे जोडलेली नाहीत. छायाचित्रे जोडल्यास नागरिक पृष्ठावर सुंदर कॅरोसेल दिसेल.
                </div>
              ) : (
                <div className="dept-gallery-grid">
                  {normalizeImages(formData.images).map((img, index, allImgs) => (
                    <div key={img.id || index} className="dept-gallery-card">
                      <div className="dept-gallery-thumb-wrap">
                        <img src={img.url} alt={img.caption || `Department Image ${index + 1}`} />
                        <span className="dept-gallery-idx-badge">#{index + 1}</span>
                      </div>
                      <div className="dept-gallery-card-body">
                        <input
                          type="text"
                          value={img.caption || ''}
                          onChange={e => updateImageCaption(img.id, e.target.value)}
                          placeholder="प्रतिमा वर्णन (Caption)"
                          className="dept-gallery-caption-input"
                        />
                        <div className="dept-gallery-actions">
                          <button
                            type="button"
                            title="वर हलवा"
                            disabled={index === 0}
                            onClick={() => moveImage(index, -1)}
                          >
                            ▲
                          </button>
                          <button
                            type="button"
                            title="खाली हलवा"
                            disabled={index === allImgs.length - 1}
                            onClick={() => moveImage(index, 1)}
                          >
                            ▼
                          </button>
                          <button
                            type="button"
                            className="danger"
                            title="हटवा"
                            onClick={() => removeImage(img.id)}
                          >
                            हटवा
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="dept-section-head department-section-head-inline">
              <div>
                <h3>सविस्तर पृष्ठ विभाग (Dynamic Public Page Sections)</h3>
                <p>विभागाच्या सार्वजनिक पृष्ठावर दिसणारे सविस्तर मजकूर, प्रतिमा आणि तक्ते येथे जोडा.</p>
              </div>
              <button type="button" className="btn-secondary" onClick={addContentSection}>+ विभाग जोडा</button>
            </div>

            {normalizeContentSections(formData.content_sections).length === 0 ? (
              <div className="dept-section-empty">
                अजून कोणताही सविस्तर विभाग जोडलेला नाही. “विभाग जोडा” वापरून माहिती जोडा.
              </div>
            ) : (
              normalizeContentSections(formData.content_sections).map((section, index, sections) => (
                <div className="dept-section-editor-card" key={section.id}>
                  <div className="dept-section-editor-top">
                    <span>{index + 1}</span>
                    <input
                      value={section.heading}
                      onChange={event => updateContentSection(section.id, 'heading', event.target.value)}
                      placeholder="विभाग शीर्षक, उदा. आरोग्य सेवा अवलोकन"
                    />
                    <button type="button" onClick={() => moveContentSection(index, -1)} disabled={index === 0}>▲</button>
                    <button type="button" onClick={() => moveContentSection(index, 1)} disabled={index === sections.length - 1}>▼</button>
                    <button type="button" className="danger" onClick={() => removeContentSection(section.id)}>हटवा</button>
                  </div>

                  <div className="dept-section-image-grid">
                    <div style={{ display: 'grid', gap: 8 }}>
                      <label className="registry-field" htmlFor={`section_image_${section.id}`}>
                        <span>Section Image URL (optional)</span>
                        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                          <input
                            id={`section_image_${section.id}`}
                            name={`section_image_${section.id}`}
                            value={section.image_url}
                            onChange={event => updateContentSection(section.id, 'image_url', event.target.value)}
                            placeholder="https://example.com/image.jpg"
                            style={{ flex: 1 }}
                          />
                          <button
                            type="button"
                            className="btn btn-outline btn-sm"
                            onClick={() => beginSectionImageUpload(section.id)}
                            disabled={sectionImageUploading}
                          >
                            {sectionImageUploading && sectionImageUploadTarget === section.id ? 'Uploading...' : 'Upload'}
                          </button>
                        </div>
                      </label>
                      <input
                        ref={sectionImageUploadRef}
                        type="file"
                        accept="image/*"
                        style={{ display: 'none' }}
                        onChange={handleSectionImageUpload}
                      />
                    </div>
                    <TextInput
                      label="Image Caption (optional)"
                      name={`section_caption_${section.id}`}
                      value={section.image_caption}
                      onChange={event => updateContentSection(section.id, 'image_caption', event.target.value)}
                      placeholder="प्रतिमा वर्णन"
                    />
                  </div>

                  <RichTextEditor
                    value={section.content}
                    onChange={value => updateContentSection(section.id, 'content', value)}
                    placeholder="विभागाची सविस्तर माहिती लिहा. Toolbar मधून सूची, प्रतिमा आणि तक्ता वापरता येईल."
                    tenantSlug={tenantSlug}
                  />
                </div>
              ))
            )}
          </div>
        </div>}

        {/* Status */}
        {currentStep === 4 && <div className="form-section staff-step-panel">
          <h3>स्थिति (Status)</h3>

          <CheckboxInput
            label="Active (सक्रिय) - Department is currently operational"
            name="is_active"
            checked={formData.is_active}
            onChange={handleChange}
          />
          <div className="staff-review-grid">
            <div><span>Department</span><strong>{formData.name || '—'}</strong></div>
            <div><span>English name</span><strong>{formData.name_english || '—'}</strong></div>
            <div><span>Department type</span><strong>{departmentTypeOptions.find(item => item.value === formData.department_type)?.label || '—'}</strong></div>
            <div><span>Department head</span><strong>{formData.head_name || 'Not assigned'}</strong></div>
            <div><span>Department images</span><strong>{normalizeImages(formData.images).length}</strong></div>
            <div><span>Required documents</span><strong>{normalizeDocuments(formData.documents_required).length}</strong></div>
            <div><span>Public content sections</span><strong>{normalizeContentSections(formData.content_sections).length}</strong></div>
          </div>
        </div>}

        {/* Actions */}
        </div>
        <div className="form-actions staff-step-actions">
          <button
            type="button"
            className="btn-secondary"
            onClick={() => navigate(`/admin/${tenantSlug}/departments`)}
          >
            Cancel
          </button>
          <div>
            {currentStep > 0 && (
              <button
                key="dept-prev-btn"
                type="button"
                className="btn-secondary"
                onClick={() => moveStep(currentStep - 1)}
              >
                ← Previous
              </button>
            )}
            {currentStep < steps.length - 1 ? (
              <button
                key="dept-next-btn"
                type="button"
                className="btn-primary"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  moveStep(currentStep + 1);
                }}
              >
                Next →
              </button>
            ) : (
              <button
                key="dept-submit-btn"
                type="submit"
                className="btn-primary"
                disabled={submitting}
              >
                {submitting ? 'Saving...' : departmentId ? 'Update Department' : 'Add Department'}
              </button>
            )}
          </div>
        </div>
      </form>
    </div>
  );
}
